import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  getListUsersQueryKey,
  useListUsers,
  useUpdateUser,
} from '@/hooks/use-api';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { UpdateUserBodyRole } from '@workspace/api-client-react';
import { format } from 'date-fns';
import { MoreVertical, ShieldAlert, ShieldCheck, UserPlus } from 'lucide-react';
import { useRef, useState } from 'react';

export default function AdminUsers() {
  const { data: users, isLoading } = useListUsers({
    query: { queryKey: getListUsersQueryKey() },
  });
  const updateUser = useUpdateUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'user' as UpdateUserBodyRole,
  });

  const updateMutationRef = useRef(updateUser.mutate);
  updateMutationRef.current = updateUser.mutate;

  const handleRoleChange = (id: number, newRole: UpdateUserBodyRole) => {
    updateMutationRef.current(
      { id, data: { role: newRole } },
      {
        onSuccess: () => {
          toast({
            title: 'User Updated',
            description: 'User role has been changed.',
          });
          queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to update user role.',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create user');
      }

      toast({ title: 'Success', description: 'User created successfully' });
      setIsCreateOpen(false);
      setCreateForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        role: 'user',
      });
      queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;

    updateMutationRef.current(
      {
        id: editingUser.id,
        data: {
          name: editingUser.name,
          phone: editingUser.phone || null,
          role: editingUser.role,
          rank: editingUser.rank || 'Rookie',
          adminMessage: editingUser.adminMessage || null,
        },
      },
      {
        onSuccess: () => {
          toast({ title: 'Success', description: 'User updated successfully' });
          setIsEditOpen(false);
          setEditingUser(null);
          queryClient.invalidateQueries({ queryKey: getListUsersQueryKey() });
        },
        onError: () => {
          toast({
            title: 'Error',
            description: 'Failed to update user',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const openEditDialog = (user: any) => {
    setEditingUser({ ...user });
    setIsEditOpen(true);
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-bebas text-4xl tracking-wide mb-2">USERS</h1>
          <p className="font-sans text-slate-500">
            Manage user accounts and permissions.
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button
              size="lg"
              className="font-rajdhani font-bold tracking-wider"
            >
              <UserPlus className="w-4 h-4 mr-2" /> CREATE USER
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="font-bebas text-2xl">
                Create New User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={createForm.name}
                  onChange={e =>
                    setCreateForm({ ...createForm, name: e.target.value })
                  }
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={createForm.email}
                  onChange={e =>
                    setCreateForm({ ...createForm, email: e.target.value })
                  }
                  placeholder="john@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={createForm.phone}
                  onChange={e =>
                    setCreateForm({ ...createForm, phone: e.target.value })
                  }
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={createForm.password}
                  onChange={e =>
                    setCreateForm({ ...createForm, password: e.target.value })
                  }
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={createForm.role}
                  onValueChange={(value: UpdateUserBodyRole) =>
                    setCreateForm({ ...createForm, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  Create User
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-bebas text-2xl">Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editingUser.name}
                  onChange={e =>
                    setEditingUser({ ...editingUser, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingUser.email}
                  onChange={e =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Phone</Label>
                <Input
                  id="edit-phone"
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={e =>
                    setEditingUser({ ...editingUser, phone: e.target.value })
                  }
                />
              </div>
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value: UpdateUserBodyRole) =>
                    setEditingUser({ ...editingUser, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-rank">Certification Rank</Label>
                <Select
                  value={editingUser.rank || 'Rookie'}
                  onValueChange={(value: string) =>
                    setEditingUser({ ...editingUser, rank: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rookie">Rookie</SelectItem>
                    <SelectItem value="Apprentice">Apprentice</SelectItem>
                    <SelectItem value="Journeyman">Journeyman</SelectItem>
                    <SelectItem value="Expert">Expert</SelectItem>
                    <SelectItem value="Master">Master</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-message">
                  Admin Message (shown on user dashboard)
                </Label>
                <Textarea
                  id="edit-message"
                  value={editingUser.adminMessage || ''}
                  onChange={e =>
                    setEditingUser({
                      ...editingUser,
                      adminMessage: e.target.value,
                    })
                  }
                  placeholder="Enter a message for this user about class updates, schedules, requirements, invoicing, etc."
                  rows={4}
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200 dark:border-slate-800 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
              <TableRow>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  NAME / EMAIL
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  PHONE
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  ROLE
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider">
                  JOINED
                </TableHead>
                <TableHead className="font-rajdhani font-bold tracking-wider text-right">
                  ACTIONS
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="h-5 w-48" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-6 w-20 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
              ) : Array.isArray(users) && users.length > 0 ? (
                users.map(user => (
                  <TableRow key={user.id} className="font-sans">
                    <TableCell>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {user.name}
                      </div>
                      <div className="text-sm text-slate-500">{user.email}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {user.phone || 'N/A'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.role === 'admin' ? (
                        <Badge className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">
                          <ShieldCheck className="w-3 h-3 mr-1" /> ADMIN
                        </Badge>
                      ) : (
                        <Badge variant="secondary">USER</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-600 dark:text-slate-400">
                        {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="font-rajdhani font-bold tracking-wider"
                        >
                          <DropdownMenuItem
                            onClick={() => openEditDialog(user)}
                          >
                            Edit User
                          </DropdownMenuItem>
                          {user.role === 'user' ? (
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(user.id, 'admin')}
                              className="text-primary focus:text-primary"
                            >
                              <ShieldCheck className="mr-2 h-4 w-4" /> Make
                              Admin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleRoleChange(user.id, 'user')}
                            >
                              <ShieldAlert className="mr-2 h-4 w-4" /> Remove
                              Admin
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-10 text-slate-500 font-sans"
                  >
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
